/**
 * Canonical class location resolver.
 *
 * Single source of truth for "where does this class meet?" across the public
 * catalog, detail modal, registration dialog, calendar, roster and Welcome
 * Packets. Nothing here is class-specific — every value is derived from the
 * class/session record.
 *
 * Fallback order:
 *   1. session-specific location / room (explicit fields, or parsed from the
 *      session `dates` text such as "… — Grace Community Church, Social Room")
 *   2. class-level location / room
 *   3. city/location fallback (only when nothing more specific exists)
 */

export const CITY_LOCATION_FALLBACK = "Boulder City, NV";

export type LocationSource = {
  location?: string | null;
  room?: string | null;
};

export type SessionLocationSource = LocationSource & {
  /** Free-form session dates text, which sometimes carries the venue. */
  dates?: string | null;
};

function clean(v: string | null | undefined): string | null {
  const s = (v ?? "").trim();
  if (!s) return null;
  if (/^(tbd|tba|n\/a|none)$/i.test(s)) return null;
  return s;
}

/** True when `haystack` already mentions `needle` (case/punctuation tolerant). */
function mentions(haystack: string, needle: string): boolean {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  return norm(haystack).includes(norm(needle));
}

/** Combine a venue and a room into one display string without duplication. */
export function joinLocationAndRoom(
  location: string | null | undefined,
  room: string | null | undefined,
): string | null {
  const loc = clean(location);
  const rm = clean(room);
  if (loc && rm) return mentions(loc, rm) ? loc : `${loc} — ${rm}`;
  return loc ?? rm ?? null;
}

/**
 * Pull a venue out of a free-form session dates string, e.g.
 * "September 11, 18, 25, 2026 (Fridays) — 4:00 PM – 5:00 PM — Grace Community Church, Social Room".
 * Returns null when the trailing segment is a time/weekday/TBD descriptor.
 */
export function locationFromSessionText(dates: string | null | undefined): string | null {
  const text = clean(dates);
  if (!text) return null;
  const parts = text.split(/—|–|--/);
  if (parts.length < 2) return null;
  const tail = clean(parts[parts.length - 1]);
  if (!tail) return null;
  if (/tbd|tba/i.test(tail)) return null;
  if (/\d{1,2}(:\d{2})?\s*(am|pm)/i.test(tail)) return null;
  if (/^(mon|tues|wednes|thurs|fri|satur|sun)day/i.test(tail)) return null;
  if (/^\d/.test(tail)) return null;
  return tail;
}

/**
 * Resolve the display location for a class, optionally scoped to a session.
 * Never returns an empty string — falls back to the city.
 */
export function resolveClassLocation(
  classSource: LocationSource | null | undefined,
  session?: SessionLocationSource | null,
): string {
  const sessionExplicit = joinLocationAndRoom(session?.location, session?.room);
  if (sessionExplicit) return sessionExplicit;

  // Structured class-level fields outrank text parsed out of the session
  // description, which is only a best-effort signal for legacy rows.
  const classLevel = joinLocationAndRoom(classSource?.location, classSource?.room);
  if (classLevel) return classLevel;

  const fromText = locationFromSessionText(session?.dates);
  if (fromText) return fromText;

  return CITY_LOCATION_FALLBACK;
}

/**
 * Location to show on a class-level surface (detail tile, card, calendar).
 * Uses a session-specific venue only when every session agrees; otherwise
 * falls back to the class-level location/room, then the city.
 */
export function resolveClassDisplayLocation(
  classSource: (LocationSource & { sessions?: SessionLocationSource[] | null }) | null | undefined,
): string {
  const classLevel = joinLocationAndRoom(classSource?.location, classSource?.room);
  const sessions = classSource?.sessions ?? [];
  if (sessions.length > 0) {
    const resolved = sessions.map((s) => {
      const explicit = joinLocationAndRoom(s.location, s.room);
      return explicit ?? (classLevel ? null : locationFromSessionText(s.dates));
    });
    const specific = resolved.filter((v): v is string => !!v);
    if (specific.length === sessions.length && new Set(specific).size === 1) {
      return specific[0];
    }
  }
  return resolveClassLocation(classSource);
}
